<template>
  <div class="box mt-4">
    <div class="d-flex justify-content-between">
      <div class="section-title">3. My Information</div>
      <div v-if="showToggle" class="mt-2">
        <som-button button-type="clear" color="primary" v-on:click.prevent="toggle">
          <som-icon name="icon-chevron-down" size="sm"></som-icon>
        </som-button>
      </div>
    </div>
    <som-collapse ref="collapse" :collapsed="collapsed">
      <div ref="phoneOrEmail" class="form-content max-width-40">
        <label class="special-label w-100">Please provide us with one of the following:</label>
        <div class="max-width-30 align-center">
          <div class="form-group">
            <label for="phoneNumber">Phone Number</label>
            <input
              type="tel"
              class="form-control"
              :class="{'is-invalid': infoErrors.phone}"
              id="phoneNumber"
              v-mask="'###-###-####'"
              v-model="phoneNumber"
            >
            <small v-if="infoErrors.phone" class="form-text text-danger">{{infoErrors.phone}}</small>
            <small v-else class="form-text text-muted">Example: 555-555-5555</small>
          </div>
          <div class="pt-1 pb-1 text-center">Or</div>
          <div class="form-group mt-0">
            <label for="email">Email</label>
            <input
              type="text"
              class="form-control"
              :class="{'is-invalid': infoErrors.email}"
              id="email"
              v-model="email"
            >
            <small v-if="infoErrors.email" class="form-text text-danger">{{infoErrors.email}}</small>
          </div>
        </div>

        <div class="mt-4 border-top-light"></div>
        <div class="max-width-30 align-center">
          <div class="form-group mt-4">
            <label for="firstName">
              First Name
              <span class="label-optional">Optional</span>
            </label>
            <input type="text" class="form-control" id="firstName" v-model="firstName">
          </div>
          <div class="form-group mt-4">
            <label for="lastName">
              Last Name
              <span class="label-optional">Optional</span>
            </label>
            <input type="text" class="form-control" id="lastName" v-model="lastName">
          </div>
          <div class="mt-4 d-flex justify-content-center">
            <som-button color="primary" v-on:click.prevent="continueFromStep3">Continue</som-button>
          </div>
        </div>
      </div>
    </som-collapse>
  </div>
</template>

<script>
// Taken from https://tylermcginnis.com/validate-email-address-javascript/
function emailIsValid(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function phoneIsValid(phone) {
  // Remove all characters that are not digits and check to see if it contains 10 digits.
  // "(555) 555-5555" becomse "5555555555" (which is a valid 10-digits number)
  return phone.replace(/[^0-9]/g, '').length === 10;
}

export default {
  name: "MyInformation",
  props: {
    collapsed: Boolean,
    showToggle: Boolean,
    toggle: Function,
    continue: Function
  },
  data() {
    return {
      // User info
      infoErrors: {},
      phoneNumber: "",
      email: "",
      firstName: "",
      lastName: ""
    };
  },
  methods: {
    show() {
      this.$refs.collapse.show();
    },
    hide() {
      this.$refs.collapse.hide();
    },

    continueFromStep3() {
      let hasError = false;
      this.infoErrors = {};

      if (this.email || this.phoneNumber) {
        // Required info was provided

        if (this.phoneNumber && !phoneIsValid(this.phoneNumber)) {
          // Phone number format is wrong
          hasError = true;
          this.infoErrors.phone = "A valid phone number must be provided.";
        }

        if (this.email && !emailIsValid(this.email)) {
          // Email format is wrong
          hasError = true;
          this.infoErrors.email = "A valid email must be provided.";
        }
      } else {
        // Missing required info
        hasError = true;
        this.infoErrors.phone = "Phone number or email is required.";
        this.infoErrors.email = "Phone number or email is required.";
      }

      if (hasError) {
        this.$refs.phoneOrEmail.scrollIntoView();
      } else {
        this.continue({
          email: this.email,
          phoneNumber: this.phoneNumber,
          firstName: this.firstName,
          lastName: this.lastName
        });
      }
    }
  },

  updated() {
    if (this.updateFocusElement) {
      this.$refs[this.updateFocusElement] &&
        this.$refs[this.updateFocusElement].focus();
      this.updateFocusElement = null;
    }
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.selected-school-box {
  display: flex;
  flex-direction: "row";
}

.selected-school-content {
  flex: 1;
}

.header-box {
  margin: 0 auto;
}

.logo-image {
  width: 100%;
  max-width: 16rem;
  height: auto;
}

.subtitle {
  font-size: 1.2rem;
}
@media screen and (max-width: 600px) {
  .subtitle {
    font-size: 1rem;
  }
}

.bold {
  font-weight: bold;
}

.box {
  background-color: white;
}

.max-width-30 {
  max-width: 30rem;
}

.max-width-40 {
  max-width: 40rem;
}

.form-content {
  padding: 2rem;
  padding-top: 0;
  margin: 0 auto;
}

.form-group {
  margin-bottom: 1rem;
}

.section-title {
  font-family: Montserrat,sans-serif;
  font-weight: 700;
  line-height: 1.1;
  font-size: 1.375rem;
  letter-spacing: .1px;
  padding: 1rem;
  margin: 0;
  color: rgb(52, 118, 163);
  white-space: nowrap; 
  overflow: hidden;
  text-overflow: ellipsis;
}
@media screen and (max-width: 600px) {
  .section-title {
    font-size: 1.2rem;
  }
}

.special-label {
  text-align: center;
  padding: 1rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid rgb(236, 236, 236);
}

textarea {
  height: 8rem;
}

textarea,
select,
input {
  font-size: 0.875rem;
}

textarea::placeholder {
  color: #aaa;
}

.message {
  margin: 0 auto;
}

.align-center {
  margin: 0 auto;
}

.border-top {
  border-top: 1px solid rgb(29, 25, 25);
}

.border-top-light {
  border-top: 1px solid rgb(236, 236, 236);
}

.text-bold {
  font-weight: bold;
}

.enter-school-information-manually {
  margin-bottom: 1rem;
}

.label-optional {
  font-size: 0.6rem;
  color: rgb(110, 110, 110);
  margin-left: 0.25rem;
}

.radio-container {
  padding: 0.5rem 1rem;
}

.radio-container.is-invalid {
  border-radius: 0.25rem;
  border: 1px solid #b80000;
}
</style>

<style>
</style>